import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography
} from '@mui/material';
import {
  Warning as WarningIcon
} from '@mui/icons-material';

const DeactivateBusinessDialog = ({ open, onClose, onConfirm, businessName, loading }) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!reason.trim()) {
      setError('Please provide a reason for deactivation');
      return;
    }

    setError('');
    try {
      await onConfirm(reason.trim());
      setReason('');
    } catch (err) {
      setError(err.message || 'Failed to deactivate business');
    }
  };

  const handleClose = () => {
    if (!loading) {
      setReason('');
      setError('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: 'error.lighter',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <WarningIcon sx={{ color: 'error.main', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Deactivate Business
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {businessName}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              This action will:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ m: 0, pl: 2 }}>
              <li>Prevent all users of this business from logging in</li>
              <li>Disable all sales and inventory operations</li>
              <li>Hide the business from active listings</li>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              The business can be reactivated later if needed.
            </Typography>
          </Alert>

          <TextField
            label="Reason for Deactivation"
            placeholder="Please explain why this business is being deactivated..."
            multiline
            rows={3}
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            disabled={loading}
            sx={{ mt: 1 }}
            helperText="This reason will be recorded and sent to the business owner"
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="error"
            disabled={loading || !reason.trim()}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ borderRadius: 2 }}
          >
            {loading ? 'Deactivating...' : 'Deactivate Business'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DeactivateBusinessDialog;
